import Controller from '@ember/controller';
import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type { Sprite } from 'ember-animated';
import { easeIn } from 'ember-animated/easings/cosine';
import move from 'ember-animated/motions/move';
import { dropTask } from 'ember-concurrency';
import type DeviceModel from 'garaje/models/device';
import type CurrentAdminService from 'garaje/services/current-admin';
import type ExporterService from 'garaje/services/exporter';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type LocationFeatureFlagsService from 'garaje/services/location-feature-flags';
import type StateService from 'garaje/services/state';
import type VisitorsOnboardingService from 'garaje/services/visitors-onboarding';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import { filterBy } from 'macro-decorators';

import type { VisitorsDevicesIpadsRouteModel } from '../route';

export default class VisitorsDevicesIpadsIndexController extends Controller {
  @service declare flashMessages: FlashMessagesService;
  @service declare visitorsOnboarding: VisitorsOnboardingService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare locationFeatureFlags: LocationFeatureFlagsService;
  @service declare currentAdmin: CurrentAdminService;
  @service declare router: RouterService;
  @service declare state: StateService;
  @service declare exporter: ExporterService;

  declare model: VisitorsDevicesIpadsRouteModel;

  get actualIpads(): DeviceModel[] {
    const showSimulators = this.locationFeatureFlags.isEnabled('visitors-devices-dashboard-show-simulators');
    return this.model.ipads.filter((device) => device.isIpad && (!device.isSimulator || showSimulators));
  }
  @filterBy('actualIpads', 'isVisible') connectedIpads!: DeviceModel[];
  @tracked showIpadDemoModal = false;
  @tracked showAccessCodeModal = false;
  @tracked accessCodeInput = '';

  deleteIpadTask = dropTask(async (ipad: DeviceModel) => {
    try {
      await ipad.destroyRecord();
      if (this.visitorsOnboarding.showVideoWalkthrough) {
        void this.visitorsOnboarding.loadIpadsTask.perform();
      }
      this.flashMessages.showAndHideFlash('success', 'Successfully deleted iPad');
    } catch (e) {
      const errorText = parseErrorForDisplay(e);
      this.flashMessages.showAndHideFlash('error', errorText);
    }
  });

  exportDevicesTask = dropTask(async () => {
    try {
      // Load all locations for devices before exporting (needed for CSV)
      await Promise.all(this.actualIpads.map((ipad) => ipad.location));

      // @ts-expect-error - exportCsv is an ember-concurrency task with perform method
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      await this.exporter.exportCsv.perform('devices.csv', this.actualIpads);
      this.flashMessages.showAndHideFlash('success', 'Devices exported successfully');
    } catch (e: unknown) {
      const errorText = parseErrorForDisplay(e);
      this.flashMessages.showAndHideFlash('error', errorText);
    }
  });

  get isVirtualFrontDesk(): boolean {
    const currentRoute = this.router.currentRouteName;
    return currentRoute.includes('virtual-front-desk');
  }

  get newIpadRoute(): string {
    return this.isVirtualFrontDesk ? 'virtual-front-desk.devices.ipads.new' : 'visitors.devices.ipads.new';
  }

  get isUserEligibleForModal(): boolean {
    return this.featureFlags.isEnabled('growth_ipad_demo');
  }

  get isLocationOrGlobalAdmin(): boolean {
    const { isGlobalAdmin, isLocationAdmin } = this.currentAdmin;
    return isGlobalAdmin || isLocationAdmin;
  }

  @action
  showModal(): void {
    if (this.featureFlags.isEnabled('growth_ipad_demo')) {
      this.showIpadDemoModal = true;
    }
  }

  @action
  closeModal(): void {
    if (this.featureFlags.isEnabled('growth_ipad_demo')) {
      this.showIpadDemoModal = false;
    }
  }

  get isAccessCodeValid(): boolean {
    return /^\d{4}$/.test(this.accessCodeInput);
  }

  @action
  openAccessCodeModal(): void {
    this.accessCodeInput = this.model.config.kioskDebugMenuPin || '';
    this.showAccessCodeModal = true;
  }

  @action
  closeAccessCodeModal(): void {
    this.showAccessCodeModal = false;
  }

  @action
  updateAccessCode(event: Event): void {
    const value = (event.target as HTMLInputElement).value.replace(/\D/g, '');
    (event.target as HTMLInputElement).value = value;
    this.accessCodeInput = value;
  }

  saveAccessCodeTask = dropTask(async () => {
    if (!this.isAccessCodeValid) {
      return;
    }

    const config = this.model.config;
    config.kioskDebugMenuPin = this.accessCodeInput;

    try {
      await config.save();
      this.flashMessages.showAndHideFlash('success', 'Saved!');
      this.showAccessCodeModal = false;
    } catch (e) {
      config.rollbackAttributes();
      const errorText = parseErrorForDisplay(e);
      this.flashMessages.showAndHideFlash('error', errorText);
    }
  });

  /* eslint-disable require-yield */
  *transition({
    keptSprites,
    removedSprites,
  }: {
    keptSprites: Sprite[];
    removedSprites: Sprite[];
  }): Generator<never, void, unknown> {
    keptSprites.forEach((sprite) => void move(sprite));
    removedSprites.forEach((sprite) => {
      sprite.endAtPixel({ x: window.innerWidth });
      void move(sprite, { easing: easeIn });
    });
  }
  /* eslint-enable require-yield */
}
